<template>
  <div class="view-wrapper account-detail-view">
    <ViewHeader title="Account Settings" :isFullViewport="true">
      <template #actions>
          <Button
            label="Close"
            class="p-button-outlined"
            @click="handleCloseAccountDetailView"
          />
        </template>
    </ViewHeader>
    <ViewMain :isFullViewport="true">
      <div class="p-mb-6">
        <p class="account-label">Account</p>
        <h3>{{ currentAccount.contactCompanyName }}</h3>
      </div>

      <div class="p-mb-6">
        <div class="p-d-flex p-jc-between p-ai-start p-mb-4">
          <div class="p-d-flex p-ai-center">
            <h3 class="p-mr-6">Account Management</h3>
            <p class="data-table-note">To transfer account ownership contact <a href="mailto:hello@annumplanning.com" class="text-link email-link">hello@annumplanning.com</a>.</p>
          </div>
        </div>
        <DataTable :value="generalInfo">
          <Column field="firstName" header="First Name" />
          <Column field="lastName" header="Last Name" />
          <Column field="email" header="Email" />
          <Column header="Role">
            <template #body> Account Owner </template>
          </Column>
        </DataTable>
      </div>

      <div class="p-mb-6">
        <div class="p-d-flex p-jc-between p-ai-end p-mb-4">
          <div class="p-d-flex p-ai-center">
            <h3 class="p-mr-6">Plans</h3>
            <p v-if="doesAccountHaveNestedPlans" class="data-table-note">
              Click on the pencil icon to edit a Plan's name or Nested Plan default acronym. Click the checkmark to save. Acronyms can be one to five characters.<br/>
              Click on the colored box to the left of the Nested Plan's name to designate it's color for differentiation within the Initiative's view only.
            </p>
            <p v-else class="data-table-note">
              Click on the pencil icon to edit a Plan's name. Click on the checkmark to save.
            </p>
          </div>
          <!-- BEGIN: Feature flagged for beta -->
          <div v-if="$store.getters.isCurrentUserSuperAdmin" class="p-d-flex">
            <Button
              label="Add Individual Plan"
              icon="pi pi-plus-circle"
              @click="() => handleCreatePlan('Individual', null)"
              class="p-button-outlined"
            ></Button>
            <Button
              label="Add Lead Plan"
              icon="pi pi-plus-circle"
              @click="() => handleCreatePlan('Master', null)"
              class="p-ml-3"
            ></Button>
          </div>
          <!-- END: Feature flagged for beta -->
        </div>
        <DataTable
          :value="currentPlans"
          class="p-mb-4"
          editMode="row"
          dataKey="id.intID"
          :editingRows.sync="planEditingRows"
          @row-edit-init="onPlanRowEditInit"
          @row-edit-save="onPlanRowEditSave"
          @row-reorder="onRowReorder"
        >
          <Column rowReorder :reorderableColumn="false" />
          <Column columnKey="name" header="Name">
            <template #body="slotProps">
              <div
                class="p-d-flex p-jc-start p-ai-center"
                :class="!!slotProps.data.parentId.intID ? 'p-ml-5 nested-plan-cell' : ''"
              >
                <ColorPicker
                    v-if="!!slotProps.data.parentId.intID"
                    class="p-mr-2"
                    @colorSelected="
                      (color, e) => {
                        slotProps.data.uiColor = color
                        openColorPickerPlanIntID = null
                        handleNestedPlanColorUpdate(e, slotProps.data)
                      }
                    "
                    @triggerClicked="
                      () => {
                        openColorPickerPlanIntID = slotProps.data.id.intID
                      }
                    "
                    :selectedColor="slotProps.data.uiColor"
                    :forcePickerClosed="openColorPickerPlanIntID !== null && openColorPickerPlanIntID !== slotProps.data.id.intID"
                    :shouldShowNestedPlanColors="true"
                  />
                {{ slotProps.data.name }}
              </div>
            </template>
            <template #editor>
              <InputText v-model="currentEditingPlan.name" />
            </template>
          </Column>
          <Column columnKey="acronym" header="Acronym" headerStyle="text-align: center;" bodyStyle="text-align: center;">
            <template #body="slotProps">
              <template v-if="slotProps.data.parentId.intID">
                {{ slotProps.data.abbreviatedName }}
              </template>
              <template v-else>
                -
              </template>
            </template>
            <template #editor="slotProps">
              <template v-if="slotProps.data.parentId.intID">
                <InputText v-model="currentEditingPlan.acronym" maxlength="5" />
              </template>
              <template v-else>
                -
              </template>
            </template>
          </Column>
          <Column columnKey="id" header="ID" headerStyle="text-align: center;" bodyStyle="text-align: center;">
            <template #body="slotProps">
              {{ slotProps.data.id.intID }}
            </template>
            <template #editor="slotProps">
              {{ slotProps.data.id.intID }}
            </template>
          </Column>
          <!-- BEGIN: Feature flagged for beta -->
          <Column
            v-if="
              $store.getters.isCurrentUserSuperAdmin
            "
          >
            <template #body="slotProps">
              <div class="p-d-flex p-jc-end">
                <Button
                  v-if="!slotProps.data.parentId.intID"
                  label="Add Nested Plan"
                  icon="pi pi-plus-circle"
                  @click="() => handleCreatePlan('Sub', slotProps.data.id)"
                ></Button>
              </div>
            </template>
          </Column>
          <!-- END: Feature flagged for beta -->
          <Column
            :rowEditor="true"
            style="width: 10%; min-width: 8rem"
            bodyStyle="text-align: right;"
          ></Column>
        </DataTable>
      </div>

      <div class="p-mb-6">
        <div class="p-d-flex p-jc-between p-ai-end p-mb-4">
          <div class="p-d-flex p-ai-center">
            <h3 class="p-mr-6">Seats</h3>
            <p :class="{'data-table-note': true, 'seat-limit-reached-message': currentAccount.seats.isAnyLimitReached}">Free up seats by removing team members from the Account. Click the pencil icon to the right of their names under Team below. Contact <a class="text-link email-link" href="mailto:hello@annumplanning.com">hello@annumplanning.com</a> to add more seats to your Account.</p>
          </div>
        </div>
        <DataTable
          :value="currentSeats"
          class="p-mb-4"
        >
          <Column field="title" header="Type"></Column>
          <Column field="used" header="Using"></Column>
          <Column field="total" header="Out Of"></Column>
          <Column field="remaining" header="Remaining"></Column>
          <Column>
            <template #body="slotProps">
              <template v-if="slotProps.data.total > 0 && slotProps.data.remaining <= 0">
                <p class="seat-limit-reached-message" style="width: 25rem;">You have reached your Account's seat limit.</p>
              </template>
              <template v-else>
                <p style="width: 25rem;"></p>
              </template>
            </template>
          </Column>
        </DataTable>
      </div>

      <div class="p-mb-6">
        <div class="p-d-flex p-jc-between p-ai-center p-mb-4">
          <div class="p-d-flex p-ai-center">
            <h3 class="p-mr-6">Team</h3>
            <p class="data-table-note">Click the pencil icon to change a team member's personal information, remove their access to a Plan, or remove them from the Account and free up their seat. Refer to our <a href="https://annumplanning.com/resources/guides/adding-and-removing-team-members/" target="_blank">guide</a> for detailed instructions.</p>
          </div>
        </div>
        <DataTable
          :value="currentAccountUsers"
          class="team-table p-mb-4"
          style="overflow-x: auto;"
        >
          <Column field="firstName" header="First Name" sortable frozen alignFrozen="left">
            <template #body="slotProps">
              <div class="p-d-flex p-ai-center">
                {{ slotProps.data.firstName }}
              </div>
            </template>
          </Column>
          <Column field="lastName" header="Last Name" sortable>
            <template #body="slotProps">
              <div class="p-d-flex p-ai-center">
                {{ slotProps.data.lastName }}
              </div>
            </template>
          </Column>
          <Column field="emailAddress" header="Email" sortable>
            <template #body="slotProps">
              <div class="p-d-flex p-ai-center">
                {{ slotProps.data.emailAddress }}
              </div>
            </template>
          </Column>
          <Column field="seatType" header="Seat Type" sortable>
            <template #body="slotProps">
              <div class="p-d-flex p-ai-center">
                {{ slotProps.data.seatType }}
              </div>
            </template>
          </Column>
          <template v-for="plan in currentAccount.plans">
            <Column :header="plan.type == nestedPlanType ? plan.abbreviatedName : plan.name" :field="plan.abbreviatedName"  headerStyle="text-align: center;" sortable>
              <template #body="slotProps">
                <div class="p-d-flex p-ai-center" :key="plan.key">
                  {{ slotProps.data[plan.abbreviatedName] }}
                </div>
              </template>
            </Column>
          </template>
          <Column field="hasHiddenTags" header="Profile Tags" sortable>
            <template #body="slotProps">
              <template v-if="slotProps.data.hasHiddenTags">
                <div class="p-d-flex p-ai-center">
                  <span class="pi pi-eye-slash"></span>
                </div>
              </template>
            </template>
          </Column>
          <Column>
            <template #body="slotProps">
              <div class="p-d-flex p-jc-end">
                <Button
                  icon="pi pi-pencil"
                  class="p-button-text"
                  title="Edit"
                  @click="onEditUserClick(slotProps.data.id)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>

      <div>
        <div class="p-mb-6 p-mt-6">
          <h3>Delete Account</h3>
          <p>
            Contact
            <a href="mailto:hello@annumplanning.com" class="text-link email-link"
              >hello@annumplanning.com</a
            >.
          </p>
        </div>
      </div>
    </ViewMain>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import Plan, {PlanType} from '@/models/Plan'
import Account from '@/models/Account'
import Role from '@/models/Role'
import {AlertMessageSeverity} from '@/models/AlertMessage'
import ID from '@/models/ID'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import UserRole from '@/models/UserRole'
import User from '@/models/User'
import OverlayPanel from 'primevue/overlaypanel'
import InputMask from 'primevue/inputmask'
import ColorPicker from '@/components/ColorPicker.vue'

Vue.component('DataTable', DataTable)
Vue.component('Column', Column)
Vue.component('OverlayPanel', OverlayPanel)
Vue.component('InputMask', InputMask)

export default Vue.extend({
  name: 'AccountDetail',
  components: {
    ViewHeader,
    ViewMain,
    ColorPicker,
  },
  data: () => {
    return {
      currentEditingPlan: {} as Plan,
      planEditingRows: [],
      updatingPlan: false as boolean,
      forceColorPickerClosed: false as boolean,
      openColorPickerPlanIntID: 0 as number|null,
    }
  },
  computed: {
    generalInfo(): {} {
      return [
        {
          firstName: this.currentAccount.contactFirstName,
          lastName: this.currentAccount.contactLastName,
          email: this.currentAccount.contactEmail,
        },
      ]
    },
    currentAccount(): Account {
      return this.$store.getters.currentAccount
    },
    canUserEditAccountProperties(): boolean {
      return this.$store.getters.currentAccountPermissionLevel <=
        Role.LEVEL_ACCOUNT_OWNER
        ? true
        : false
    },
    canUserManagePlansAndUsers(): boolean {
      return this.$store.getters.currentAccountPermissionLevel <=
        Role.LEVEL_ACCOUNT_OWNER
        ? true
        : false
    },
    currentPlans(): Plan[] {
      return this.$store.getters.allPlans
    },
    doesAccountHaveNestedPlans(): boolean {
      return this.currentPlans.filter((plan) => plan.parentId.intID !== 0).length ? true : false
    },
    accountOwnerId(): ID {
      const ownerUserRole = this.$store.getters.currentAccount.userRoles.find((userRole)=> userRole.roleId.intID == 1)
      return ownerUserRole ? ownerUserRole.userId : new ID()
    },
    currentSeats(): {}[] {
      const currentSeatsObj = this.$store.getters.currentAccount.seats

      let seatsArray = [
        {
          title:  'Admin / Contributor',
          used: currentSeatsObj.admin.current,
          total: currentSeatsObj.admin.total,
          remaining: currentSeatsObj.adminRemaining,
        },
      ]
      if(!currentSeatsObj.isAdminOnlyAccount){
        seatsArray = [
          {
            title:  'Admin / Contributor',
            used: currentSeatsObj.admin.current,
            total: currentSeatsObj.admin.total,
            remaining: currentSeatsObj.adminRemaining,
          },
          {
            title:  'Viewer',
            used: currentSeatsObj.viewer.current,
            total: currentSeatsObj.viewer.total,
            remaining: currentSeatsObj.viewerRemaining,
          },
        ]
      }
      if(currentSeatsObj.isUniversalAccount){
        seatsArray = [
          {
            title:  'Any Role',
            used: currentSeatsObj.universal.current,
            total: currentSeatsObj.universal.total,
            remaining: currentSeatsObj.universalRemaining,
          },
        ]
      }
      // Integration seats are a separate pool - shown below Viewer, hidden when none exist
      if(currentSeatsObj.integration.total > 0){
        seatsArray.push({
          title:  'Integration',
          used: currentSeatsObj.integration.current,
          total: currentSeatsObj.integration.total,
          remaining: currentSeatsObj.integrationRemaining,
        })
      }
      return seatsArray
    },
    currentAccountUsers(): {}[] {
      const accountUsers = this.currentAccount.users
      const accountPlans = this.currentAccount.plans
      const accountUsersArray = [] as {}[]
      accountUsers.forEach((accountUser) => {
        const thisAccountUserObject = {
          id: accountUser.id.clone(),
          firstName: accountUser.firstName,
          lastName: accountUser.lastName,
          emailAddress: accountUser.emailAddress,
          seatType: this.getSeatTypeForUser(accountUser),
          hasHiddenTags: this.doesUserHaveHiddenTags(accountUser),
        }
        accountPlans.forEach((plan) => {
          thisAccountUserObject[`${plan.abbreviatedName}`] = this.getPlanRoleForUser(accountUser, plan)
        })
        accountUsersArray.push(thisAccountUserObject)
      })
      return accountUsersArray
    },
    nestedPlanType(): string {
      return PlanType.Sub
    }
  },
  methods: {
    handleNestedPlanColorUpdate(e, plan: Plan) {
      e.stopPropagation()

      if (!this.updatingPlan) {
        plan.accountId = this.$store.getters.currentAccount.id
        this.updatingPlan = true
        setTimeout(() => {
          this.$store.getters.services.plans.update(plan).then(
            (updatedPlan) => {
              this.updatingPlan = false

              // Update color on plan object in global store
              const thisPlanInStore = this.$store.getters.allPlans.find((plan)=>plan.id.intID === updatedPlan.id.intID)
              if(thisPlanInStore){
                thisPlanInStore.uiColor = updatedPlan.uiColor
              }
            }
          )
        }, 400)
      }
    },
    doesUserHaveHiddenTags(inputUser: User): boolean {
      return inputUser.blockedTagIds.length > 0
    },
    getPlanRoleForUser(inputUser: User, inputPlan: Plan): string {
      if(inputUser.id.intID === this.currentAccount.ownerId.intID){
        return 'Acct Owner'
      }
      const planRoleName = this.currentAccount.userRoles.find((userRole) => userRole.userId.intID === inputUser.id.intID && userRole.planId.intID === inputPlan.id.intID)?.role.title 
      return planRoleName ? planRoleName : ''
    },
    getSeatTypeForUser(inputUser: User): string {
      const userRolesForThisUser = this.currentAccount.userRoles.filter((userRole) => userRole.userId.intID === inputUser.id.intID)

      // Integration is a separate seat pool - takes precedence over universal labeling
      if(userRolesForThisUser.some((userRole) => userRole.role && userRole.role.isIntegration)){
        return 'Integration'
      }

      if(this.$store.getters.currentAccount.seats.isUniversalAccount){
        return 'Universal'
      }

      let calculatedSeatType = 'Unknown'
      const userRoleWithLowestLevel = userRolesForThisUser.reduce((lowestRole, currentRole) => {
          const lowestLevel = lowestRole && lowestRole.role && typeof lowestRole.role.level === 'number' ? lowestRole.role.level : Infinity
          const currentLevel = currentRole && currentRole.role && typeof currentRole.role.level === 'number' ? currentRole.role.level : Infinity
          if (currentLevel < lowestLevel) {
            return currentRole;
          }
          return lowestRole;
        }, userRolesForThisUser[0])

      if(userRoleWithLowestLevel && userRoleWithLowestLevel.role){
        if(userRoleWithLowestLevel.role.level < Role.LEVEL_VIEWER){
          calculatedSeatType = 'Admin / Contributor'
        } else {
          calculatedSeatType = 'Viewer'
        }
      }

      return calculatedSeatType
    },
    handleCloseAccountDetailView(){
      if(window.history.length > 2){
        this.$router.back()
      }else{
        this.$router.replace('/')
      }
    },
    handleCreatePlan(planType: PlanType | string, parentId: ID | null) {
      const newPlan = new Plan()
      newPlan.name = 'New Plan'
      newPlan.type = planType as PlanType
      newPlan.accountId = this.$store.getters.currentAccount.id
      if (parentId) {
        newPlan.parentId = parentId
      }
      newPlan.creator = this.$store.getters.currentUser

      this.$store.getters.services.plans
        .create(newPlan, newPlan.type == PlanType.Sub ? false : true)
        .then(
          (responsePlan) => {
            this.$store.dispatch('updateCurrentPlan', responsePlan)
            this.$store.getters.currentAccount.plans.push(responsePlan)

            // Add nested plan ID to nested plans array on local lead plan
            const parentPlan = this.$store.getters.currentAccount.plans.find((plan)=>plan.id.intID === responsePlan.parentId?.intID)
            if(parentPlan){
              parentPlan.plans.push(responsePlan.id)
            }

            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.success,
              summary: 'Plan created.',
              life: 3000,
            })

            const planAdminRole = this.$store.getters.allRoles.find(
              (role) => role.name === 'Admin'
            )

            // Create UserRole for Account Owner.
            const createAccountOwnerUserRole = () => {
              const newUserRoleAccountOwner = new UserRole()
              newUserRoleAccountOwner.roleId = planAdminRole
                ? planAdminRole.id
                : ID.fromResponseObject(4, 'roles')
              newUserRoleAccountOwner.userId = this.accountOwnerId
              newUserRoleAccountOwner.accountId = this.$store.getters.currentAccount.id
              newUserRoleAccountOwner.planId = responsePlan.id
              newUserRoleAccountOwner.canViewBudget = true
              newUserRoleAccountOwner.channels = responsePlan.channels.map(
                (channel) => channel.id
              )
              newUserRoleAccountOwner.canEditAllChannels = true

              this.$store.getters.services.users.createUserRole(newUserRoleAccountOwner).then(
                (userRoleResponse) => {
                  this.$store.getters.currentPlan.userRoles.push(userRoleResponse)
                  this.$store.getters.currentAccount.userRoles.push(userRoleResponse)

                  Vue.prototype.$toast.add({
                    severity: AlertMessageSeverity.success,
                    summary: 'Account Owner added to new plan.',
                    life: 3000,
                  })
                },
                (error) => {
                  Vue.prototype.$toast.add({
                    severity: AlertMessageSeverity.error,
                    summary: 'Error adding account owner to new plan.',
                  })
                }
              )
            }

            createAccountOwnerUserRole()
          },
          (error) => {
            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.error,
              summary: 'Error creating plan.',
            })
          }
        )
    },
    getAccountUserForId(userId: ID): User {
      const thisUser = this.$store.getters.allAccountUsers.find(
        (user) => user.id?.intID == userId.intID
      )
      return thisUser ? thisUser : new User()
    },
    onEditUserClick(userId: ID) {
      this.$router.push({
        path: `/account/user/${userId.intID}`,
      })
    },
    onPlanRowEditInit(event) {
      this.currentEditingPlan.name = event.data.name
      this.currentEditingPlan.acronym = event.data.acronym
    },
    onPlanRowEditSave(event) {
      if (this.currentEditingPlan.name === event.data.name && this.currentEditingPlan.acronym === event.data.acronym) {
        return
      }

      const thisPlan = event.data as Plan
      thisPlan.accountId = this.$store.getters.currentAccount.id
      thisPlan.name = this.currentEditingPlan.name
      thisPlan.acronym = this.currentEditingPlan.acronym

      this.$store.getters.services.plans.update(thisPlan).then(
        (plan) => {
          event.data.name = plan.name
          event.data.acronym = plan.acronym

          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Plan updated.',
            life: 3000,
          })
        },
        (error) => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: 'Error updating plan.',
          })
        }
      )
    },
    onRowReorder(data) {
      const plansList = data.value as Plan[]
      const targetPlan = plansList[data.dropIndex] as Plan
      const parentIntID = targetPlan.parentId.intID

      const siblingPlans = plansList.filter(
        (plan: Plan) => plan.parentId.intID === parentIntID
      )

      const plansToUpdate: Plan[] = siblingPlans.map(
        (plan: Plan, index: number) => {
          plan.orderIndex = index
          return plan
        }
      )

      plansToUpdate.forEach((plan: Plan) => {
        plan.accountId = this.$store.getters.currentAccount.id
        this.$store.getters.services.plans.update(plan).then(
          (updatedPlan) => {

            // Update orderIndex on plan object in global store
            const thisPlanInStore = this.$store.getters.allPlans.find((plan)=>plan.id.intID === updatedPlan.id.intID)
            if(thisPlanInStore){
              thisPlanInStore.orderIndex = updatedPlan.orderIndex
            }

            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.success,
              summary: 'Plan order updated.',
              life: 3000,
            })
          },
          () => {
            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.error,
              summary: 'Error updating plan order.',
            })
          }
        )
      })
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.account-detail-view {
  .view-main {
    h3 {
      margin-bottom: 0;
      @include font-mulish-light;
      font-size: 2rem;
    }
    p {
      &.account-label {
        margin-bottom: 0;
        color: #333333;
      }
      &.data-table-note {
        max-width: 75rem;
        color: #999;
      }
      &.seat-limit-reached-message {
        color: red;
      }
    }
    a {
      &.email-link {
        text-decoration: underline;
      }
    }
  }

  // Show color picker panel
  .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
    overflow: visible;
  }

  // Plans table - nested plan reordering
  .p-datatable-wrapper {
    td {
      // Set width of reorder handle column
      &:has(.p-datatable-reorderablerow-handle) {
        width: 0rem;
        padding: 0;
      }
    }
    tr {
      // Hide reorder handle for all plans that are not nested
      &:not(:has(.nested-plan-cell)) {
        .p-datatable-reorderablerow-handle {
          display: none;
          pointer-events: none;
        }
      }
      .p-datatable-reorderablerow-handle {
        transform: translateX(2.5rem);
      }
    }
  }

  .team-table {
    thead th:first-child,
    tbody td:first-child,
    thead th:nth-child(2),
    tbody td:nth-child(2) {
      position: sticky;
      left: 0;
      z-index: 1;
    }
    thead th:nth-child(2),
    tbody td:nth-child(2) {
      left: 64px;
    }
    tbody td:first-child,
    tbody td:nth-child(2) {
      background: #fff;
    }

    .p-sortable-column {
      .p-sortable-column-icon {
        opacity: 0;
      }

      &:hover,
      &:focus {
        .p-sortable-column-icon {
          opacity: 1;
        }
      }
    }
  }
}
</style>
